import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

/**
 * Beta Notice Page
 * Explains beta status, known issues, and how users can help
 */
const BetaNotice = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-spooky-bg-darker py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="text-6xl mb-4"
          >
            🚧
          </motion.div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Boop is in Beta
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            We're actively building and improving. Here's what you should know.
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-6">
          {/* What Beta Means */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>🎯</span>
              What "Beta" Means
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Boop is currently in beta testing, which means we're still actively developing and refining the platform.
              While the core features work well, you may occasionally encounter:
            </p>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300 ml-6">
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span><strong>Bugs and glitches</strong> - Some features may not work as expected</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span><strong>Performance issues</strong> - Pages may load slowly during peak times</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span><strong>Incomplete features</strong> - Some functionality is still being built</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span><strong>Design changes</strong> - The UI/UX may evolve based on feedback</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span><strong>Data migration</strong> - In rare cases, we may need to update the database structure</span>
              </li>
            </ul>
          </section>

          {/* What Works Well */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>✨</span>
              What Works Well
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Despite being in beta, these core features are stable and ready to use:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <span className="font-semibold text-gray-900 dark:text-white">✅ Browse & Search Prompts</span>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <span className="font-semibold text-gray-900 dark:text-white">✅ Create & Edit Prompts</span>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <span className="font-semibold text-gray-900 dark:text-white">✅ Rate & Review</span>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <span className="font-semibold text-gray-900 dark:text-white">✅ Collections</span>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <span className="font-semibold text-gray-900 dark:text-white">✅ User Profiles</span>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <span className="font-semibold text-gray-900 dark:text-white">✅ Gamification System</span>
              </div>
            </div>
          </section>

          {/* Known Issues */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>🐛</span>
              Known Issues We're Working On
            </h2>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-r-lg">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Email notifications</strong> - Coming soon! You'll receive emails for badges, awards, and activity.
                </p>
              </div>
              <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-r-lg">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Mobile experience</strong> - We're continuously improving responsive design for smaller screens.
                </p>
              </div>
              <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-r-lg">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Search filters</strong> - Advanced search features are being refined based on user feedback.
                </p>
              </div>
            </div>
          </section>

          {/* How You Can Help */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>💪</span>
              How You Can Help
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Your feedback is invaluable during this beta phase! Here's how you can contribute:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">📝</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Report Bugs</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Found something broken? Let us know! Open an issue on{' '}
                    <a
                      href="https://github.com/petec4244/Boop/issues"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 dark:text-purple-400 hover:underline"
                    >
                      GitHub
                    </a>
                    {' '}with details about what happened.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">💡</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Suggest Features</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Have an idea for improvement? We'd love to hear it! Share your suggestions on GitHub.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">🎨</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Share Your Experience</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Tell us what you love and what could be better. Your usage patterns help us prioritize development.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">🚀</span>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Spread the Word</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Love Boop? Share it with other prompt engineers, AI enthusiasts, and creators!
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>📅</span>
              What's Next
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              We're working hard to move from beta to full launch. Here's our roadmap:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-purple-500 rounded-full flex-shrink-0"></div>
                <span className="text-gray-700 dark:text-gray-300">
                  <strong>Now:</strong> Beta testing with early adopters (that's you!)
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-purple-400 rounded-full flex-shrink-0"></div>
                <span className="text-gray-700 dark:text-gray-300">
                  <strong>Next 2 weeks:</strong> Email system, mobile improvements, bug fixes
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-gray-300 dark:bg-gray-600 rounded-full flex-shrink-0"></div>
                <span className="text-gray-700 dark:text-gray-300">
                  <strong>Launch:</strong> Public release with all features stable and polished
                </span>
              </div>
            </div>
          </section>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors duration-200"
          >
            <span>👻</span>
            <span>Back to Boop</span>
          </Link>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Thanks for being part of our beta community!
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default BetaNotice;
